/** Internal progress data. None of these signals are console/Sentry records. */
export type WorkCategory =
	| 'load'
	| 'resolution'
	| 'parameter'
	| 'graph'
	| 'editor'
	| 'material_viewport'
	| 'preview'
	| 'asset'
	| 'compile'
	| 'export'
	| 'query'
	| 'preparation'
	| 'animation'
	| 'boot';
export type WorkStage =
	| 'input_preparation_pending'
	| 'worker_operation_pending'
	| 'worker_unresponsive'
	| 'asset_wait_pending'
	| 'render_progress_pending'
	| 'gpu_completion_pending'
	| 'presentation_pending'
	| 'query_result_pending'
	| 'unknown';
export type WorkTrigger =
	| 'no_progress'
	| 'unexpected_exception'
	| 'worker_terminal'
	| 'command_rejected';
export type WorkEnd =
	| 'project_closed'
	| 'document_replaced'
	| 'account_changed'
	| 'worker_disposed'
	| 'page_exit';
export type WorkToken = {
	projectId: number;
	generation: number;
	category: WorkCategory;
	sequence: number;
	ordinal?: number;
};
export type WorkSignal = {
	token: WorkToken;
	stage: WorkStage;
	progress: number;
	done?: boolean;
	failed?: boolean;
	presentation?: number;
	settled?: number;
};
export type InputQueueSnapshot = {
	depth: number;
	preparing: number;
	running: boolean;
	oldestAgeMs: number;
};
export type WorkSnapshot = {
	serialPending: number;
	serialRunning: boolean;
	oldestSerialAgeMs: number;
	lastReceived: number;
	lastExecuted: number;
	gpuPending: boolean;
	gpuAgeMs: number;
	graphCompleted: number;
	graphSubmitted: number;
	editorSequence: number;
	assetsPending: boolean;
	loadingStatus: string;
	loadCompleted: number;
	resolution: number | null;
	previousResolution: number | null;
	workerReady: boolean;
	recovering: boolean;
};
export type HealthRecord = {
	name:
		| 'aqueduct.health.problem'
		| 'aqueduct.health.continued_activity'
		| 'aqueduct.health.recovered'
		| 'aqueduct.health.ended';
	level: 'info' | 'warn' | 'error';
	attributes: Record<string, string | number | boolean>;
	error?: Error;
};
type Lane = {
	token: WorkToken;
	stage: WorkStage;
	since: number;
	progress: number;
	attempts: number;
	dispatched: boolean;
};
type Incident = {
	id: string;
	trigger: WorkTrigger;
	reason: WorkStage;
	category: WorkCategory;
	started: number;
	activityAt: number | null;
	activity: number;
	reportedActivity: boolean;
	failures: number;
	later: string[];
	successful: boolean;
	initialOperation: number | null;
	rejected: number;
	gestures: Set<number>;
};
type Owner = {
	projectId: number;
	generation: number;
	documentId: string | null;
	lanes: Map<WorkCategory, Lane>;
	incident: Incident | null;
	history: Array<{ at: number; event: string }>;
	historyDropped: number;
	snapshot: WorkSnapshot | null;
	snapshotAt: number;
	inputQueue: InputQueueSnapshot | null;
	held: boolean;
	issued: Map<WorkCategory, number>;
};

export const HEALTH_INTERVAL_MS: number = 1000;
export const HEALTH_STALL_MS: number = 5000;

const RENDERED_CATEGORIES: ReadonlySet<WorkCategory> = new Set([
	'load',
	'resolution',
	'parameter',
	'graph',
	'animation'
]);

/** Page-owned state machine; fake-clock tests use exactly the production logic.
 * One bounded lane per semantic category coalesces ordered rendering demand.
 * Queries/preparations use a group barrier in the caller (all results must settle).
 */
export class WorkHealth {
	private owners: Map<number, Owner> = new Map();
	private sequence: number = 0;
	private visible: boolean = true;
	private hiddenSince: number | null = null;
	private excludedTime: number = 0;
	private lastTick: number;
	private probe: { id: number; at: number } | null = null;
	private nextProbe: number = 0;
	private lastProbeAt: number = -Infinity;
	private workerFailed: boolean = false;
	constructor(
		private readonly emit: (record: HealthRecord) => void,
		private readonly now: () => number = () => performance.now(),
		private readonly id: () => string = () => crypto.randomUUID()
	) {
		this.lastTick = now();
	}
	private observedNow(): number {
		return (this.hiddenSince ?? this.now()) - this.excludedTime;
	}

	register(projectId: number, generation: number, documentId: string | null = null): void {
		const prior: Owner | undefined = this.owners.get(projectId);
		if (prior?.generation === generation) {
			prior.documentId = documentId;
			return;
		}
		if (prior) this.end(projectId, 'document_replaced');
		this.owners.set(projectId, {
			projectId,
			generation,
			documentId,
			lanes: new Map(),
			incident: null,
			history: [],
			historyDropped: 0,
			snapshot: null,
			snapshotAt: 0,
			inputQueue: null,
			held: false,
			issued: new Map()
		});
	}
	begin(
		projectId: number,
		category: WorkCategory,
		stage: WorkStage = 'input_preparation_pending',
		activity: boolean | number = true,
		worker: boolean = false
	): WorkToken {
		const owner: Owner = this.owners.get(projectId)!;
		const token: WorkToken = {
			projectId,
			generation: owner.generation,
			category,
			sequence: ++this.sequence,
			...(worker ? { ordinal: (owner.issued.get(category) ?? 0) + 1 } : {})
		};
		if (token.ordinal !== undefined) owner.issued.set(category, token.ordinal);
		const lane: Lane | undefined = owner.lanes.get(category);
		if (lane) {
			lane.token = token;
			lane.attempts++;
		} else
			owner.lanes.set(category, {
				token,
				stage,
				since: this.observedNow(),
				progress: 0,
				attempts: 1,
				dispatched: false
			});
		this.remember(owner, `requested:${category}`);
		if (activity) {
			for (const incident of [
				owner.incident,
				projectId === 0 ? null : this.owners.get(0)?.incident
			]) {
				if (!incident || (typeof activity === 'number' && incident.gestures.has(activity)))
					continue;
				if (typeof activity === 'number') {
					incident.gestures.add(activity);
					if (incident.gestures.size > 64)
						incident.gestures.delete(incident.gestures.values().next().value!);
				}
				incident.activity++;
				incident.activityAt ??= this.observedNow();
			}
		}
		return token;
	}
	dispatched(token: WorkToken, stage: WorkStage = 'worker_operation_pending'): void {
		const lane: Lane | undefined = this.owner(token)?.lanes.get(token.category);
		if (!lane || token.sequence < lane.token.sequence) return;
		lane.dispatched = true;
		this.advance(lane, stage, 0);
	}
	signal(signal: WorkSignal): void {
		const owner: Owner | undefined = this.owner(signal.token);
		const lane: Lane | undefined = owner?.lanes.get(signal.token.category);
		if (!owner || !lane) return;
		if (signal.progress > lane.progress) this.advance(lane, signal.stage, signal.progress);
		else if (signal.token.sequence >= lane.token.sequence) lane.stage = signal.stage;
		if (
			!signal.done ||
			(signal.settled === undefined && signal.token.sequence < lane.token.sequence) ||
			(signal.settled !== undefined &&
				signal.settled < (owner.issued.get(signal.token.category) ?? 0))
		)
			return;
		owner.lanes.delete(signal.token.category);
		this.remember(owner, `${signal.failed ? 'failed' : 'completed'}:${signal.token.category}`);
		if (
			!signal.failed &&
			owner.incident &&
			this.relevant(owner.incident.category, signal.token.category)
		)
			owner.incident.successful = true;
		this.recover(owner);
		const worker: Owner | undefined = this.owners.get(0);
		if (!signal.failed && worker?.incident && !this.workerFailed) {
			worker.incident.successful = true;
			if (
				![...this.owners.values()].some((o: Owner) =>
					[...o.lanes.keys()].some((category: WorkCategory) => category !== 'animation')
				)
			)
				this.recover(worker);
		}
	}
	progress(token: WorkToken, stage: WorkStage, progress: number): void {
		this.signal({ token, stage, progress });
	}
	complete(token: WorkToken, failed: boolean = false): void {
		this.signal({ token, stage: 'unknown', progress: 0, done: true, failed });
	}
	failure(
		projectId: number,
		category: WorkCategory,
		stage: WorkStage,
		error: unknown,
		trigger: WorkTrigger = 'unexpected_exception'
	): void {
		const owner: Owner | undefined = this.owners.get(projectId);
		if (!owner) return;
		if (
			projectId !== 0 &&
			this.owners.get(0)?.incident &&
			(trigger === 'command_rejected' || this.workerFailed)
		) {
			if (trigger === 'command_rejected') this.owners.get(0)!.incident!.rejected++;
			return;
		}
		if (owner.incident) {
			owner.incident.failures++;
			if (trigger === 'command_rejected') owner.incident.rejected++;
			owner.incident.successful = false;
			const summary: string = this.summary(error);
			if (owner.incident.later.length < 4 && !owner.incident.later.includes(summary))
				owner.incident.later.push(summary);
			return;
		}
		this.problem(owner, trigger, stage, category, error);
	}
	terminal(error: unknown): void {
		this.workerFailed = true;
		if (!this.owners.has(0)) this.register(0, 1);
		this.failure(0, 'boot', 'worker_unresponsive', error, 'worker_terminal');
	}
	hold(projectId: number, held: boolean): void {
		const owner: Owner | undefined = this.owners.get(projectId);
		if (!owner) return;
		if (owner.held !== held)
			for (const lane of owner.lanes.values()) lane.since = this.observedNow();
		owner.held = held;
	}
	ongoing(projectId: number, completed: number, enabled: boolean): void {
		const owner: Owner | undefined = this.owners.get(projectId);
		if (!owner) return;
		if (!enabled) {
			owner.lanes.delete('animation');
			return;
		}
		if (!owner.lanes.has('animation'))
			this.begin(projectId, 'animation', 'render_progress_pending', false);
		const lane: Lane = owner.lanes.get('animation')!;
		lane.dispatched = true;
		if (completed > lane.progress) {
			this.advance(lane, 'render_progress_pending', completed);
			if (owner.incident && this.relevant(owner.incident.category, 'animation'))
				owner.incident.successful = true;
			this.recover(owner);
			const worker: Owner | undefined = this.owners.get(0);
			if (
				!this.workerFailed &&
				worker?.incident &&
				![...this.owners.values()].some((o: Owner) =>
					[...o.lanes.keys()].some((category: WorkCategory) => category !== 'animation')
				)
			) {
				worker.incident.successful = true;
				this.recover(worker);
			}
		}
	}

	setVisible(visible: boolean): void {
		if (visible === this.visible) return;
		if (!visible) this.hiddenSince = this.now();
		else {
			this.excludedTime += this.now() - (this.hiddenSince ?? this.now());
			this.hiddenSince = null;
		}
		this.visible = visible;
		this.resetObservation();
	}
	private resetObservation(): void {
		this.lastTick = this.now();
		this.probe = null;
		this.lastProbeAt = -Infinity;
		for (const owner of this.owners.values())
			for (const lane of owner.lanes.values()) lane.since = this.observedNow();
	}
	get active(): boolean {
		return (
			this.visible &&
			[...this.owners.values()].some(
				(o: Owner) =>
					(!o.held && o.lanes.size > 0) ||
					(o.incident !== null && o.incident.activityAt !== null && !o.incident.reportedActivity)
			)
		);
	}
	tick(sendProbe: (id: number) => void): void {
		if (!this.visible) return;
		const wallNow: number = this.now();
		// A suspended/busy page cannot testify that the worker was unresponsive.
		if (wallNow - this.lastTick > HEALTH_INTERVAL_MS * 3) {
			this.excludedTime += wallNow - this.lastTick;
			this.resetObservation();
		}
		this.lastTick = wallNow;
		const now: number = this.observedNow();
		const dispatched: boolean = [...this.owners.values()].some(
			(o: Owner) => !o.held && [...o.lanes.values()].some((l: Lane) => l.dispatched)
		);
		if (
			dispatched &&
			!this.probe &&
			now - this.lastProbeAt >= HEALTH_INTERVAL_MS &&
			!this.workerFailed
		) {
			this.probe = { id: ++this.nextProbe, at: now };
			this.lastProbeAt = now;
			sendProbe(this.probe.id);
		}
		if (
			dispatched &&
			this.probe &&
			now - this.probe.at >= HEALTH_STALL_MS &&
			!this.owners.get(0)?.incident
		) {
			if (!this.owners.has(0)) this.register(0, 1);
			this.problem(this.owners.get(0)!, 'no_progress', 'worker_unresponsive', 'query');
		}
		for (const owner of this.owners.values()) {
			const lane: Lane | undefined = [...owner.lanes.values()].sort(
				(a: Lane, b: Lane) => a.since - b.since
			)[0];
			if (
				!owner.held &&
				lane &&
				now - lane.since >= HEALTH_STALL_MS &&
				!owner.incident &&
				!(lane.dispatched && this.owners.get(0)?.incident)
			) {
				// Allow the outstanding probe its complete observation interval first.
				if (!lane.dispatched || !this.probe || now - this.probe.at < HEALTH_INTERVAL_MS * 2)
					this.problem(owner, 'no_progress', lane.stage, lane.token.category);
			}
			const incident: Incident | null = owner.incident;
			if (
				incident &&
				!incident.reportedActivity &&
				incident.activityAt !== null &&
				now - incident.activityAt >= HEALTH_INTERVAL_MS
			) {
				incident.reportedActivity = true;
				this.record(owner, 'aqueduct.health.continued_activity', 'warn');
			}
		}
	}
	probeAnswered(id: number): void {
		if (this.probe?.id === id) this.probe = null;
	}
	end(projectId: number, reason: WorkEnd): void {
		const owner: Owner | undefined = this.owners.get(projectId);
		if (!owner) return;
		if (owner.incident)
			this.record(owner, 'aqueduct.health.ended', 'info', { 'diagnostic.end_reason': reason });
		this.owners.delete(projectId);
	}
	endAll(reason: WorkEnd): void {
		for (const projectId of this.owners.keys()) this.end(projectId, reason);
	}
	input(projectId: number, snapshot: InputQueueSnapshot): void {
		const owner: Owner | undefined = this.owners.get(projectId);
		if (owner) owner.inputQueue = snapshot;
	}

	snapshot(projectId: number, generation: number, snapshot: WorkSnapshot): void {
		const owner: Owner | undefined = this.owners.get(projectId);
		if (!owner || owner.generation !== generation) return;
		owner.snapshot = snapshot;
		owner.snapshotAt = this.observedNow();
	}

	private relevant(failed: WorkCategory, completed: WorkCategory): boolean {
		return (
			failed === completed ||
			(RENDERED_CATEGORIES.has(failed) && RENDERED_CATEGORIES.has(completed))
		);
	}

	private owner(token: WorkToken): Owner | undefined {
		const owner: Owner | undefined = this.owners.get(token.projectId);
		return owner?.generation === token.generation ? owner : undefined;
	}
	private advance(lane: Lane, stage: WorkStage, progress: number): void {
		if (
			progress > lane.progress ||
			(lane.stage === 'input_preparation_pending' && stage !== lane.stage)
		)
			lane.since = this.observedNow();
		lane.stage = stage;
		lane.progress = Math.max(progress, lane.progress);
	}
	private remember(owner: Owner, event: string): void {
		owner.history.push({ at: this.observedNow(), event });
		if (owner.history.length > 32) {
			owner.history.shift();
			owner.historyDropped++;
		}
	}
	private summary(error: unknown): string {
		return (error instanceof Error ? `${error.name}: ${error.message}` : String(error)).slice(
			0,
			256
		);
	}
	private problem(
		owner: Owner,
		trigger: WorkTrigger,
		reason: WorkStage,
		category: WorkCategory,
		error?: unknown
	): void {
		owner.incident = {
			id: this.id(),
			trigger,
			reason,
			category,
			started: this.observedNow(),
			activityAt: null,
			activity: 0,
			reportedActivity: false,
			failures: error === undefined ? 0 : 1,
			later: [],
			successful: false,
			initialOperation: owner.lanes.get(category)?.token.sequence ?? null,
			rejected: trigger === 'command_rejected' ? 1 : 0,
			gestures: new Set()
		};
		this.record(
			owner,
			'aqueduct.health.problem',
			trigger === 'no_progress' ? 'warn' : 'error',
			{},
			error instanceof Error
				? error
				: error === undefined
					? undefined
					: new Error(this.summary(error))
		);
	}
	private recover(owner: Owner): void {
		if (
			!owner.incident?.successful ||
			[...owner.lanes.keys()].some((category: WorkCategory) => category !== 'animation')
		)
			return;
		this.record(owner, 'aqueduct.health.recovered', 'info');
		owner.incident = null;
	}
	private record(
		owner: Owner,
		name: HealthRecord['name'],
		level: HealthRecord['level'],
		extra: Record<string, string | number | boolean> = {},
		error?: Error
	): void {
		const incident: Incident = owner.incident!;
		const affected: Owner[] =
			owner.projectId === 0
				? [...this.owners.values()].filter((o: Owner) => o.projectId !== 0 && o.lanes.size > 0)
				: [owner];
		const snapshot = {
			history: owner.history.map((entry) => ({ ...entry, at: entry.at - incident.started })),
			historyDropped: owner.historyDropped,
			initialOperation: incident.initialOperation,
			worker: owner.snapshot,
			input: owner.inputQueue,
			laterFailures: incident.later,
			affected: affected.slice(0, 8).map((o: Owner) => ({
				projectId: o.projectId,
				generation: o.generation,
				snapshot: owner.projectId === 0 ? o.snapshot : undefined,
				ageMs: o.snapshot ? this.observedNow() - o.snapshotAt : null
			})),
			pending: [...owner.lanes.values()].map((l: Lane) => ({
				category: l.token.category,
				sequence: l.token.sequence,
				stage: l.stage,
				progress: l.progress,
				attempts: l.attempts,
				ageMs: Math.max(0, this.observedNow() - l.since)
			}))
		};
		const encoder: TextEncoder = new TextEncoder();
		let snapshotJson: string = JSON.stringify(snapshot);
		while (encoder.encode(snapshotJson).byteLength > 16 * 1024 && snapshot.history.length > 0) {
			snapshot.history.shift();
			snapshot.historyDropped++;
			snapshotJson = JSON.stringify(snapshot);
		}

		// Remaining fields are fixed-size host scalars and bounded failure summaries.
		this.emit({
			name,
			level,
			error,
			attributes: {
				'event.name': name,
				'diagnostic.schema_version': 1,
				'aqueduct.worker_generation': 1,
				'diagnostic.operation_id': incident.initialOperation ?? 0,
				'diagnostic.rejected_after_problem': incident.rejected,
				'diagnostic.evidence_age_ms': owner.snapshot
					? Math.max(0, this.observedNow() - owner.snapshotAt)
					: -1,
				'diagnostic.no_progress_ms': Math.max(
					0,
					this.observedNow() - (owner.lanes.get(incident.category)?.since ?? this.observedNow())
				),
				'diagnostic.stage': {
					input_preparation_pending: 'input',
					worker_operation_pending: 'worker',
					worker_unresponsive: 'worker',
					asset_wait_pending: 'assets',
					render_progress_pending: 'render',
					gpu_completion_pending: 'gpu',
					presentation_pending: 'presentation',
					query_result_pending: 'query',
					unknown: 'unknown'
				}[incident.reason],
				'diagnostic.incident_id': incident.id,
				'diagnostic.owner': owner.projectId === 0 ? 'worker' : 'project',
				'aqueduct.project_instance_id': owner.projectId,
				'aqueduct.document_generation': owner.generation,
				...(owner.documentId ? { 'aqueduct.document_id': owner.documentId.slice(0, 128) } : {}),
				'diagnostic.trigger': incident.trigger,
				'diagnostic.reason': incident.reason,
				'diagnostic.operation_kind': incident.category,
				'diagnostic.incident_duration_ms': Math.max(0, this.observedNow() - incident.started),
				'diagnostic.attempts_after_problem': incident.activity,
				'diagnostic.failure_count': incident.failures,
				'diagnostic.affected_count': affected.length,
				'diagnostic.snapshot_json': snapshotJson,
				...extra
			}
		});
	}
}
